import { Root as RadixTabsRoot } from '@radix-ui/react-tabs';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import type { TabsProps as RadixTabRootProps } from '@radix-ui/react-tabs';
import { isNotNil } from '@wistia/type-guards';
import { TabsValueProvider } from './useTabsValue';
import { SelectedItemStyleProvider } from '../SegmentedControl/useSelectedItemStyle';

export type TabsProps = Pick<
  RadixTabRootProps,
  'children' | 'defaultValue' | 'onValueChange' | 'value'
>;

export const Tabs = ({
  children,
  value: valueProp,
  onValueChange: onValueChangeProp,
  defaultValue,
  ...props
}: TabsProps): JSX.Element => {
  // We track value internally so that even if the component is uncontrolled, we
  // can pass it to the TabsValueProvider
  const [value, setValue] = useState(valueProp ?? defaultValue);

  const onValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (onValueChangeProp) {
        onValueChangeProp(newValue);
      }
    },
    [onValueChangeProp],
  );

  const rootProps: TabsProps = {
    ...props,
    onValueChange,
  };

  if (isNotNil(value)) {
    rootProps.value = value;
  }

  if (isNotNil(defaultValue)) {
    rootProps.defaultValue = defaultValue;
  }

  return (
    <RadixTabsRoot {...rootProps}>
      <TabsValueProvider value={value ?? defaultValue}>
        <SelectedItemStyleProvider>{children}</SelectedItemStyleProvider>
      </TabsValueProvider>
    </RadixTabsRoot>
  );
};

Tabs.displayName = 'Tabs';
